import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, FolderIcon, ClipboardDocumentListIcon, NoSymbolIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { TrackerEmail, EmailProcessRequest, AffaireCreationStats } from '../../tracker/types/tracker.types';
import { emailsAPI } from '../../tracker/api/emails.api';
import { tasksAPI } from '../../mes-taches/api/tasks.api';
import { User } from '../../mes-taches/types/task.types';
import { AttachmentSelector } from './AttachmentSelector';

interface ProcessEmailModalProps {
  email: TrackerEmail;
  onClose: () => void;
  onSuccess?: () => void;
}

interface MatchedContact {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface MatchedAffaire {
  id: number;
  status: string;
  type: string;
  expert: string;
  date: string;
  address: string;
  role: string;
}

interface ContactWithAffaires {
  contact: MatchedContact;
  affaires: MatchedAffaire[];
}

type ActionType = 'add_to_dossier' | 'create_task';

export const ProcessEmailModal: React.FC<ProcessEmailModalProps> = ({
  email,
  onClose,
  onSuccess
}) => {
  // Action type toggle
  const [actionType, setActionType] = useState<ActionType>('add_to_dossier');

  // Add to dossier state - now supports multiple affaires
  const [affaireIds, setAffaireIds] = useState<string[]>([]);
  const [currentAffaireInput, setCurrentAffaireInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client matching state - now supports multiple contacts
  const [allContacts, setAllContacts] = useState<ContactWithAffaires[]>([]);
  const [currentContactIndex, setCurrentContactIndex] = useState(0);
  const [isLoadingMatch, setIsLoadingMatch] = useState(false);

  // Derived state for current contact
  const matchedContact = allContacts[currentContactIndex]?.contact || null;
  const matchedAffaires = allContacts[currentContactIndex]?.affaires || [];

  // Attachment selection state (for filtering which attachments appear in tracker documents)
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>([]);

  // Task creation state
  const [assignedUsers, setAssignedUsers] = useState<string[]>([]);
  const [noAssignment, setNoAssignment] = useState(false); // For "à assigner" tasks
  const [taskMessage, setTaskMessage] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isAffaireCreation, setIsAffaireCreation] = useState(false);
  const [affaireCreationStats, setAffaireCreationStats] = useState<AffaireCreationStats>({});
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Fetch users for task assignment
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await tasksAPI.getAllUsers();
        if (response.success) {
          // Filter to only bureau and admin users
          const filteredUsers = response.data.filter(
            (u: User) => u.role === 'admin' || u.role === 'bureau'
          );
          setUsers(filteredUsers);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch affaire creation stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await emailsAPI.getAffaireCreationStats();
        if (response.success) {
          setAffaireCreationStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching affaire creation stats:', error);
      }
    };
    fetchStats();
  }, []);

  // Initialize task message with email subject
  useEffect(() => {
    if (email.subject) {
      setTaskMessage(`Email: ${email.subject}`);
    }
  }, [email.subject]);

  // Match sender email to client
  useEffect(() => {
    const matchSender = async () => {
      if (!email.sender_address) return;

      setIsLoadingMatch(true);
      try {
        const token = localStorage.getItem('authToken');
        const baseURL = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
        const response = await fetch(
          `${baseURL}/api/admin/emails/match-sender?email=${encodeURIComponent(email.sender_address)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.matched && result.data) {
            // Use allContacts if available, otherwise fall back to single contact format
            if (result.data.allContacts && result.data.allContacts.length > 0) {
              setAllContacts(result.data.allContacts);
            } else {
              // Backwards compatibility with single contact response
              setAllContacts([{
                contact: result.data.contact,
                affaires: result.data.affaires || []
              }]);
            }
            setCurrentContactIndex(0);
          }
        }
      } catch (error) {
        console.error('Error matching sender:', error);
      } finally {
        setIsLoadingMatch(false);
      }
    };

    matchSender();
  }, [email.sender_address]);

  // Navigation between contacts
  const goToPrevContact = () => {
    setCurrentContactIndex(prev =>
      prev <= 0 ? allContacts.length - 1 : prev - 1
    );
  };

  const goToNextContact = () => {
    setCurrentContactIndex(prev =>
      prev >= allContacts.length - 1 ? 0 : prev + 1
    );
  };

  // Helper to generate unique ID for attachments (same logic as AttachmentSelector)
  const getAttachmentUniqueId = (att: { id?: string; name: string }, idx: number) => {
    return att.id || `attachment-${idx}-${att.name}`;
  };

  // Initialize all attachments as selected
  useEffect(() => {
    if (email.attachments && email.attachments.length > 0) {
      // Filter out inline attachments and get unique IDs
      const attachmentIds = email.attachments
        .filter(att => !att.isInline && !att.is_inline)
        .map((att, idx) => getAttachmentUniqueId(att, idx));
      setSelectedAttachmentIds(attachmentIds);
    }
  }, [email.attachments]);

  // Toggle affaire selection (add/remove from array)
  const handleSelectAffaire = (id: number) => {
    const idStr = id.toString();
    setAffaireIds(prev => {
      if (prev.includes(idStr)) {
        return prev.filter(a => a !== idStr);
      }
      return [...prev, idStr];
    });
  };

  // Add affaire from manual input
  const handleAddAffaire = () => {
    const trimmed = currentAffaireInput.trim().replace(/\D/g, '');
    if (trimmed.length >= 4 && !affaireIds.includes(trimmed)) {
      setAffaireIds([...affaireIds, trimmed]);
      setCurrentAffaireInput('');
    }
  };

  // Remove affaire from selection
  const handleRemoveAffaire = (id: string) => {
    setAffaireIds(affaireIds.filter(a => a !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (actionType === 'add_to_dossier') {
        // PATH 1: Add to dossier

        if (affaireIds.length === 0) {
          alert('Veuillez sélectionner au moins une affaire');
          setIsSubmitting(false);
          return;
        }

        // Calculate excluded attachments (those NOT selected)
        let excludedIds: string[] = [];
        if (email.attachments && email.attachments.length > 0) {
          const allSelectableWithIds = email.attachments
            .filter(att => !att.isInline && !att.is_inline)
            .map((att, idx) => ({ uniqueId: getAttachmentUniqueId(att, idx) }));
          excludedIds = allSelectableWithIds
            .filter(att => !selectedAttachmentIds.includes(att.uniqueId))
            .map(att => att.uniqueId);
        }

        if (affaireIds.length === 1) {
          // Single affaire - use existing processEmail endpoint
          const requestData: EmailProcessRequest = {
            affaire_id: parseInt(affaireIds[0])
          };
          if (excludedIds.length > 0) {
            requestData.excluded_attachments = excludedIds;
          }
          const response = await emailsAPI.processEmail(email.id, requestData);
          if (!response.success) {
            throw new Error('Failed to process email');
          }
        } else {
          // Multiple affaires - use assignToMultipleAffaires endpoint
          const response = await emailsAPI.assignToMultipleAffaires(email.id, {
            affaire_ids: affaireIds.map(id => parseInt(id)),
            excluded_attachments: excludedIds.length > 0 ? excludedIds : undefined
          });
          if (!response.success) {
            throw new Error('Failed to assign email to multiple affaires');
          }
        }

        onSuccess?.();
        onClose();
      } else {
        // PATH 2: Create task (new flow)
        if (assignedUsers.length === 0 && !noAssignment) {
          alert('Veuillez sélectionner un utilisateur ou choisir "Personne (à assigner)"');
          setIsSubmitting(false);
          return;
        }

        // Send request to process email (creates task without affaire_id)
        const requestData: EmailProcessRequest = {
          comment: taskMessage || `Email: ${email.subject || '(Sans objet)'}`,
          needs_assignment: noAssignment, // Will be true if "Personne" was selected
          is_affaire_creation: isAffaireCreation
        };

        // Only add assigned_to if a user was selected
        if (assignedUsers.length > 0) {
          requestData.assigned_to = assignedUsers[0]; // Primary assignee
        }

        const response = await emailsAPI.processEmail(email.id, requestData);

        if (response.success) {
          onSuccess?.();
          onClose();
        } else {
          alert('Erreur lors de la création de la tâche');
        }
      }
    } catch (error) {
      console.error('Error processing email:', error);
      alert('Erreur lors du traitement de l\'email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isBanning, setIsBanning] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      const response = await emailsAPI.discardEmail(email.id, { reason: 'Archived from modal' });
      if (response.success) {
        onSuccess?.();
        onClose();
      } else {
        alert('Erreur lors de l\'archivage');
      }
    } catch (error) {
      console.error('Error archiving email:', error);
      alert('Erreur lors de l\'archivage de l\'email');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleBanSender = async () => {
    if (!email.sender_address) return;
    const confirmed = window.confirm(
      `Bloquer "${email.sender_address}" ?\n\nTous les emails de cet expéditeur seront archivés et les futurs emails seront automatiquement ignorés.`
    );
    if (!confirmed) return;

    setIsBanning(true);
    try {
      const response = await emailsAPI.banSender(email.sender_address, 'Spam');
      if (response.success) {
        onSuccess?.();
        onClose();
      } else {
        alert('Erreur lors du blocage');
      }
    } catch (error) {
      console.error('Error banning sender:', error);
      alert('Erreur lors du blocage de l\'expéditeur');
    } finally {
      setIsBanning(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content process-email-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Traiter l'email</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Action Type Toggle */}
        <div className="process-email-action-toggle">
          <button
            type="button"
            className={`action-toggle-btn ${actionType === 'add_to_dossier' ? 'active' : ''}`}
            onClick={() => setActionType('add_to_dossier')}
          >
            <FolderIcon className="action-toggle-icon" />
            Ajouter au dossier
          </button>
          <button
            type="button"
            className={`action-toggle-btn ${actionType === 'create_task' ? 'active' : ''}`}
            onClick={() => setActionType('create_task')}
          >
            <ClipboardDocumentListIcon className="action-toggle-icon" />
            Créer une tâche
          </button>
        </div>

        <form onSubmit={handleSubmit} className="process-email-form">
          {actionType === 'add_to_dossier' ? (
            <>
              {/* Client Match Section */}
              {isLoadingMatch ? (
                <div className="form-group">
                  <div className="match-loading">Recherche du client...</div>
                </div>
              ) : matchedContact ? (
                <div className="form-group match-section">
                  <div className="match-header">
                    <span className="match-icon">✓</span>
                    <span className="match-title">Client identifié</span>
                    {/* Navigation between contacts when multiple */}
                    {allContacts.length > 1 && (
                      <div className="match-navigation">
                        <button
                          type="button"
                          className="match-nav-btn"
                          onClick={goToPrevContact}
                          title="Client précédent"
                        >
                          <ChevronLeftIcon className="match-nav-icon" />
                        </button>
                        <span className="match-nav-counter">
                          {currentContactIndex + 1} / {allContacts.length}
                        </span>
                        <button
                          type="button"
                          className="match-nav-btn"
                          onClick={goToNextContact}
                          title="Client suivant"
                        >
                          <ChevronRightIcon className="match-nav-icon" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="match-contact">
                    <strong>{matchedContact.name}</strong>
                    {matchedContact.email && <span className="match-email">{matchedContact.email}</span>}
                    {matchedContact.phone && <span className="match-phone">{matchedContact.phone}</span>}
                  </div>
                  {matchedAffaires.length > 0 && (
                    <div className="match-affaires">
                      <label>Dossiers du client ({matchedAffaires.length}) - Cliquez pour sélectionner</label>
                      <div className="match-affaires-list">
                        {matchedAffaires.map((aff) => (
                          <button
                            key={aff.id}
                            type="button"
                            className={`match-affaire-item ${affaireIds.includes(aff.id.toString()) ? 'selected' : ''}`}
                            onClick={() => handleSelectAffaire(aff.id)}
                          >
                            <span className="affaire-id">{aff.id}</span>
                            <span className="affaire-address">{aff.address || 'Adresse non disponible'}</span>
                            <span className="affaire-status">{aff.status}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Selected Affaires Chips */}
              {affaireIds.length > 0 && (
                <div className="form-group">
                  <label>Dossiers sélectionnés ({affaireIds.length})</label>
                  <div className="selected-affaires-chips">
                    {affaireIds.map(id => (
                      <span key={id} className="affaire-chip">
                        #{id}
                        <button
                          type="button"
                          className="affaire-chip-remove"
                          onClick={() => handleRemoveAffaire(id)}
                          title="Retirer"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="affaire-id">
                  {affaireIds.length > 0 ? 'Ajouter un autre dossier' : 'Numéro d\'affaire'}
                </label>
                <div className="affaire-input-row">
                  <input
                    id="affaire-id"
                    type="text"
                    value={currentAffaireInput}
                    onChange={(e) => setCurrentAffaireInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="Ex: 12345"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAffaire();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-add-affaire"
                    onClick={handleAddAffaire}
                    disabled={currentAffaireInput.length < 4 || affaireIds.includes(currentAffaireInput)}
                  >
                    + Ajouter
                  </button>
                </div>
                {affaireIds.length === 0 && (
                  <small className="form-hint">
                    L'email sera lié à cette affaire et apparaîtra dans le Tracker
                  </small>
                )}
                {affaireIds.length > 1 && (
                  <small className="form-hint form-hint-info">
                    L'email sera dupliqué dans {affaireIds.length} dossiers
                  </small>
                )}
              </div>

              {/* Attachment Selection - only show when email has attachments and affaire is provided */}
              {email.has_attachments && email.attachments && email.attachments.length > 0 && affaireIds.length > 0 && (
                <AttachmentSelector
                  attachments={email.attachments}
                  emailId={email.id}
                  selectedIds={selectedAttachmentIds}
                  onSelectionChange={setSelectedAttachmentIds}
                />
              )}
            </>
          ) : (
            <>
              {/* Task Creation Form */}
              <div className="form-group">
                <label>Attribuer à</label>
                {isLoadingUsers ? (
                  <div className="process-email-loading">Chargement...</div>
                ) : (
                  <div className="user-dropdown-compact">
                    <button
                      type="button"
                      className="user-dropdown-compact__trigger"
                      onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    >
                      <span className="user-dropdown-compact__value">
                        {noAssignment ? 'Personne (à assigner)' :
                         assignedUsers[0] ? users.find(u => u.id === assignedUsers[0])?.first_name + ' ' + users.find(u => u.id === assignedUsers[0])?.last_name :
                         '-- Sélectionner --'}
                      </span>
                      <svg className={`user-dropdown-compact__arrow ${isUserDropdownOpen ? 'user-dropdown-compact__arrow--open' : ''}`} width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isUserDropdownOpen && (
                      <div className="user-dropdown-compact__list">
                        <div
                          className={`user-dropdown-compact__item user-dropdown-compact__item--none ${noAssignment ? 'user-dropdown-compact__item--selected' : ''}`}
                          onClick={() => { setNoAssignment(true); setAssignedUsers([]); setIsUserDropdownOpen(false); }}
                        >
                          Personne (à assigner)
                        </div>
                        {[...users].sort((a, b) => {
                          const aStats = affaireCreationStats[a.id]?.last7days || 0;
                          const bStats = affaireCreationStats[b.id]?.last7days || 0;
                          return bStats - aStats;
                        }).map((user) => {
                          const stats = affaireCreationStats[user.id];
                          return (
                            <div
                              key={user.id}
                              className={`user-dropdown-compact__item ${assignedUsers[0] === user.id ? 'user-dropdown-compact__item--selected' : ''}`}
                              onClick={() => { setNoAssignment(false); setAssignedUsers([user.id]); setIsUserDropdownOpen(false); }}
                            >
                              <span>{user.first_name} {user.last_name}</span>
                              {stats && (
                                <span className="user-dropdown-compact__stats">
                                  <span className="user-dropdown-compact__stat--active">{stats.active}</span>
                                  <span className="user-dropdown-compact__stat--week">{stats.last7days}</span>
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Affaire Creation Checkbox */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isAffaireCreation}
                    onChange={(e) => setIsAffaireCreation(e.target.checked)}
                  />
                  <span>Création d'affaire (nouvelle demande)</span>
                </label>
                <small className="form-hint">
                  Cochez si cette tâche concerne la création d'un nouveau dossier
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="task-message">Message de la tâche *</label>
                <textarea
                  id="task-message"
                  value={taskMessage}
                  onChange={(e) => setTaskMessage(e.target.value)}
                  placeholder="Décrivez la tâche..."
                  rows={4}
                  className="task-message-input"
                  required
                />
                <small className="form-hint">
                  La tâche sera créée sans lien vers un dossier et apparaîtra dans Mes Tâches
                </small>
              </div>
            </>
          )}

          <div className="pem-actions">
            <div className="pem-actions-left">
              <button
                type="button"
                className="pem-ban-btn"
                onClick={handleBanSender}
                disabled={isBanning || !email.sender_address}
                title={`Bloquer ${email.sender_address || 'cet expéditeur'}`}
              >
                <NoSymbolIcon className="pem-ban-icon" />
                {isBanning ? 'Blocage...' : 'Bloquer l\'expéditeur'}
              </button>
              <button
                type="button"
                className="pem-archive-btn"
                onClick={handleArchive}
                disabled={isArchiving}
                title="Archiver cet email"
              >
                <ArchiveBoxIcon className="pem-archive-icon" />
                {isArchiving ? 'Archivage...' : 'Archiver'}
              </button>
            </div>
            <div className="pem-actions-right">
              <button type="button" className="pem-cancel-btn" onClick={onClose}>
                Annuler
              </button>
              <button
                type="submit"
                className="pem-submit-btn"
                disabled={
                  isSubmitting ||
                  (actionType === 'add_to_dossier' && affaireIds.length === 0) ||
                  (actionType === 'create_task' && assignedUsers.length === 0 && !noAssignment)
                }
              >
                {isSubmitting
                  ? 'Traitement...'
                  : actionType === 'add_to_dossier'
                    ? 'Ajouter au dossier'
                    : 'Créer la tâche'
                }
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
